import { ListItemButton } from '@mui/material';
import { dayjsNow } from '@/Infrastructure/Utilities/dayjs.type';
import { getEnumValueByKey } from '@/Infrastructure/Utilities';
import { usePutLegsMutation } from '@api/Trinium/Endpoints/Leg/slice';
import { LegStatus } from '@api/Trinium/types';
import { batchLegLabel, StatusActionProps } from '@components/DataGrid/Dispatch/types';
import { DispatchLeg } from '@api/Trinium/Endpoints/Dispatch/types';
import DisabledTooltip from '@components/DisabledTooltip';
import { appendErrorAnnouncement } from '@/Infrastructure/Components/Announcements/AnnouncementSlice';
import { getApiErrorMessage } from '@api/getApiError';
import useCurrentTerminal from '@hooks/useCurrentTerminal';
import { useAppDispatch } from '@reduxStore';

/**
 * Deliver Now Action List Item
 */
export default function DeliverAction({ leg, onError, selectedRows }: StatusActionProps) {
  const appDispatch = useAppDispatch();
  const { number: terminalNumber } = useCurrentTerminal();
  const [putLegs] = usePutLegsMutation();

  /**
   * This action requires Leg to not have PU (Picked Up) status
   * */
  if (getEnumValueByKey(LegStatus, leg.legStatus) !== LegStatus.PU) {
    return <></>;
  }

  const canDeliver = (row: DispatchLeg) =>
    getEnumValueByKey(LegStatus, row.legStatus) === LegStatus.PU && row.deliverDate === undefined && Boolean(row.drvId);

  const candidateRows = selectedRows?.some((selectedRow) => selectedRow.legId === leg.legId) ? selectedRows : [leg];
  const targetRows = candidateRows.filter(canDeliver);

  /** The clicked leg gates the action so the row the dispatcher is looking at is never silently skipped */
  const validation = {
    disabled: !canDeliver(leg),
    disabledReason: () => {
      if (leg.deliverDate !== undefined) {
        return 'Leg has already been delivered';
      }

      if (!leg.drvId) {
        return 'Driver not currently assigned';
      }

      return undefined;
    }
  };

  const handleBtnClick = () => {
    void putLegs({
      terminalNumber,
      actionType: {
        action: 'deliver',
        data: {
          legIds: targetRows.map((targetRow) => targetRow.legId),
          deliveryDate: dayjsNow().format('YYYY-MM-DDTHH:mm:00.000+00:00')
        }
      }
    })
      .unwrap()
      .then((response) => {
        response.errors.forEach((error) => {
          if (onError) onError(error.errorMessage);
          else appDispatch(appendErrorAnnouncement({ message: error.errorMessage }));
        });
      })
      .catch((error: unknown) => {
        const errorMessage = getApiErrorMessage(error);
        if (onError) onError(errorMessage);
        else appDispatch(appendErrorAnnouncement({ message: errorMessage }));
      });
  };

  return (
    <DisabledTooltip message={validation.disabledReason()}>
      <ListItemButton
        onClick={handleBtnClick}
        disabled={validation.disabled}>
        {batchLegLabel('Deliver Now', targetRows.length)}
      </ListItemButton>
    </DisabledTooltip>
  );
}
